import React, { Component } from "react";
import { compose } from "recompose";
import { Redirect } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Input from "@material-ui/core/Input";

import Button from "../components/Button";
import ErrorMessage from "../components/ErrorMessage";
import withGeneralTranslations from "../hocs/withGeneralTranslations";
import Heading4 from "../styleguide/Heading4";

const LOGIN_ENDPOINT = `${process.env.REACT_APP_API_URI}/login`;

const styles = (theme) => ({
    containerStyle: {
        flex: "1 0 auto",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "1200px",
        width: "100%",
        [theme.breakpoints.down("xs")]: {
            width: "100%",
        },
    },
    formWrapperStyle: {
        marginTop: "10%",
        maxWidth: 450,
        marginLeft: "auto",
        marginRight: "auto",
    },
    formStyle: {
        display: "flex",
        flexDirection: "column",
        padding: "15px 15px 5px",
    },
    inputRootStyle: {
        padding: 0,
        "label + &": {
            marginTop: 46,
        },
    },
    inputStyle: {
        borderRadius: 0,
        backgroundColor: "#fff",
        border: "1px solid #d6d8db",
        color: "#303c49",
        fontSize: theme.typography.pxToRem(15),
        padding: "10px",
        width: "calc(100% - 20px)",
        transition: theme.transitions.create(["border-color", "box-shadow"]),
        fontFamily: ["-apple-system", "BlinkMacSystemFont", "Arial", "sans-serif"].join(","),
        "&:focus": {
            borderColor: "#303C49",
            boxShadow: "inset 0 1px 1px rgba(0,0,0,.075),0 0 8px rgba(48,60,73,.6)",
        },
    },
    labelStyle: {
        fontSize: theme.typography.pxToRem(15),
        fontFamily: ["-apple-system", "BlinkMacSystemFont", "Arial", "sans-serif"].join(","),
        fontWeight: "bold",
    },
    submitButtonRootStyle: {
        marginTop: 20,
    },
    submitButtonStyle: {
        fontSize: theme.typography.pxToRem(15),
        fontFamily: "MAN-light, Arial, sans-serif",
        color: "#e2e3e4",
        backgroundColor: "#303C49",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#303C49",
        borderRadius: 0,
        padding: "10px 15px",
        textAlign: "center",
        verticalAlign: "middle",
        cursor: "pointer",
        lineHeight: 1.428571429,
        height: "auto",
        "-webkit-appearance": "none",
        "-moz-appearance": "none",
        "&:hover": {
            backgroundColor: "#1c232a",
            borderColor: "#181e24",
        },
    },
    submitButtonDisabledStyle: {
        cursor: "not-allowed",
        opacity: 0.65,
        boxShadow: "none",
    },
    errorStyle: {
        padding: 15,
    },
    registerButton: {
        borderRadius: 0,
    },
});

class Login extends Component {
    state = {
        username: "",
        password: "",
        error: "",
        redirectToReferrer: false,
        loginPending: false,
    };

    handleChange = (prop) => (event) => {
        this.setState({ [prop]: event.target.value });
    };

    handleSubmit = (event) => {
        event.preventDefault();

        const { username, password } = this.state;
        const { generalTranslations } = this.props;

        if (username === "" || password === "") {
            return this.setState({
                error: generalTranslations("login-error-wrong-credentials"),
            });
        }

        fetch(LOGIN_ENDPOINT, {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        })
            .then((res) => {
                if (res.status === 200) {
                    return res.json();
                }

                const error =
                    res.status === 401 ? generalTranslations("login-error-wrong-credentials") : "General error";

                return { error };
            })
            .then(({ error, token }) => {
                if (error) {
                    return this.setState({ error, loginPending: false });
                }

                // save token
                localStorage.setItem("token", token);
                // redirect to original location
                this.setState({ redirectToReferrer: true, loginPending: false });
            })
            .catch((error) => {
                // console.log('fetch error', error)
            });

        this.setState({ password: "", error: "", loginPending: true });
    };

    render() {
        const { from } = this.props.location.state || { from: { pathname: "/" } };
        const {
            classes: {
                containerStyle,
                formWrapperStyle,
                formStyle,
                inputStyle,
                inputRootStyle,
                labelStyle,
                submitButtonRootStyle,
                submitButtonStyle,
                submitButtonDisabledStyle,
                errorStyle,
                registerButton,
            },
            generalTranslations,
        } = this.props;

        if (this.state.redirectToReferrer) {
            return <Redirect to={from} />;
        }

        return (
            <div className={containerStyle}>
                <div className={formWrapperStyle}>
                    <form className={formStyle} onSubmit={this.handleSubmit} action="#">
                        <Heading4>{generalTranslations("login-intro")}</Heading4>
                        <TextField
                            label={generalTranslations("login-user")}
                            id="username-input"
                            value={this.state.username}
                            disabled={this.state.loginPending}
                            onChange={this.handleChange("username")}
                            InputProps={{
                                disableUnderline: true,
                                classes: {
                                    root: inputRootStyle,
                                    input: inputStyle,
                                },
                            }}
                            InputLabelProps={{
                                shrink: false,
                                className: labelStyle,
                            }}
                        />
                        <TextField
                            type="password"
                            label={generalTranslations("login-password")}
                            id="password-input"
                            value={this.state.password}
                            disabled={this.state.loginPending}
                            onChange={this.handleChange("password")}
                            InputProps={{
                                disableUnderline: true,
                                classes: {
                                    root: inputRootStyle,
                                    input: inputStyle,
                                },
                            }}
                            InputLabelProps={{
                                shrink: false,
                                className: labelStyle,
                            }}
                        />
                        <Input
                            type="submit"
                            value="Login"
                            disableUnderline={true}
                            disabled={this.state.loginPending}
                            classes={{
                                root: submitButtonRootStyle,
                                input: submitButtonStyle,
                                disabled: submitButtonDisabledStyle,
                            }}
                        />
                    </form>
                    {this.state.error && (
                        <div className={errorStyle}>
                            <ErrorMessage message={this.state.error} />
                        </div>
                    )}

                    <div className={errorStyle}>
                        <Button
                            className={registerButton}
                            classes={{ root: registerButton }}
                            label={generalTranslations("login-register")}
                            href={"https://mybus.man.eu/pages/register"}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default compose(withGeneralTranslations, withStyles(styles))(Login);



// WEBPACK FOOTER //
// ./src/screens/Login.js